import { enumBoxShapes, enumChartRenderOrder } from "../constants";
import { QElement } from "./QElement";
import { SVG_CreateBoxPath } from "../utils/SVG_CreateBoxPath";
import { XML_DrawBoxGroupHighlight } from "../utils/XML_DrawBoxGroupHighlight";

export class QBoxGroup extends QElement {
  constructor() {
    super();
    this.fPolyList = null;
    this.fID = -1;
    this.fColor = null;
    this.fParentPanel = null;
    this.fLayout = null;
    this.fAcceptsClicks = false;
  }

  UpdateBoxGroupAsElement(parentPanel) {
    this.fParentPanel = parentPanel;

    //No element type is set.
    this.SetZPlane(enumChartRenderOrder.groups);
  }

  SetLayout(layout) {
    this.fLayout = layout;
  }

  GetLayout() {
    return this.fLayout;
  }

  /**
   * @return {boolean}
   */
  IsSelectable() {
    return false;
  }

  SetGroupID(groupID) {
    this.fID = groupID;
  }

  GetGroupID() {
    return this.fID;
  }

  SetColor(color) {
    this.fColor = color;
  }

  AddPoly(poly) {
    if (!this.fPolyList) {
      this.fPolyList = [];
    }

    var p = poly.Clone();
    this.fPolyList.push(p);
  }

  /**
   * @return {number}
   */
  GetWidth() {
    var nPoly = this.fPolyList.length;
    var maxX = Number.NEGATIVE_INFINITY;
    var minX = Number.POSITIVE_INFINITY;
    for (var i = 0; i < nPoly; i++) {
      var poly = this.fPolyList[i];
      var tempLeft = poly.fSides[poly.fLeftMosts[0]].p1.x;
      var tempRight = poly.fSides[poly.fRightMosts[0]].p1.x;
      if (tempLeft < minX) minX = tempLeft;
      if (tempRight > maxX) maxX = tempRight;
    }
    return Math.abs(maxX - minX);
  }

  /**
   * @return {number}
   */
  GetHeight() {
    var nPoly = this.fPolyList.length;
    var topY = Number.POSITIVE_INFINITY;
    var bottomY = Number.NEGATIVE_INFINITY;
    for (var i = 0; i < nPoly; i++) {
      var poly = this.fPolyList[i];
      var tempTop = poly.fSides[poly.fTopMosts[0]].p1.y;
      var tempBottom = poly.fSides[poly.fBottomMosts[0]].p1.y;
      if (tempTop < topY) topY = tempTop;
      if (tempBottom > bottomY) bottomY = tempBottom;
    }
    return Math.abs(bottomY - topY);
  }

  GetPositionX() {
    var nPoly = this.fPolyList.length;
    var minX = Number.POSITIVE_INFINITY;
    for (var i = 0; i < nPoly; i++) {
      var poly = this.fPolyList[i];
      var tempLeft = poly.fSides[poly.fLeftMosts[0]].p1.x;
      if (tempLeft < minX) minX = tempLeft;
    }
    return this.GetXOffset() + minX;
  }

  GetPositionY() {
    var nPoly = this.fPolyList.length;
    var topY = Number.POSITIVE_INFINITY;
    for (var i = 0; i < nPoly; i++) {
      var poly = this.fPolyList[i];
      var tempTop = poly.fSides[poly.fTopMosts[0]].p1.y;
      if (tempTop < topY) topY = tempTop;
    }
    return topY + this.GetYOffset();
  }

  /**
   * @return {number}
   */
  GetXOffset() {
    var layout = this.GetLayout();
    var positionX = layout.GetPositionX();

    return positionX;
  }

  /**
   * @return {number}
   */
  GetYOffset() {
    var layout = this.GetLayout();
    var positionY = layout.GetPositionY();

    return positionY;
  }

  /**
   * @return {boolean}
   */
  IsShown() {
    return true;
  }

  DoRenderElement(drawBuffer, altRender, altCanvasElement) {
    this.DoRenderBoxGroup(drawBuffer, altRender, altCanvasElement);
  }

  DoRenderBoxGroup(drawBuffer, altRender, altCanvasElement) {
    if (!this.fPolyList) {
      return;
    }

    var nPoly = this.fPolyList.length;
    for (var i = 0; i < nPoly; i++) {
      var poly = this.fPolyList[i];
      this.RenderPoly(poly, drawBuffer, altCanvasElement);
    }
  }

  RenderPoly(poly, drawBuffer, altCanvasElement) {
    //var fillStyle = "rgba(" + this.fColor[0] + ", " + this.fColor[1] + ", " + this.fColor[2] + ", " + this.fColor[3] + ")";
    var fillStyle = this.fColor;

    var xOffset = this.GetXOffset();
    var yOffset = this.GetYOffset();

    if (drawBuffer !== null) {
      var ctx = drawBuffer.getContext("2d");
      ctx.save();
      ctx.globalAlpha = 0.3;
      ctx.strokeStyle = fillStyle; //"#000000";
      ctx.fillStyle = fillStyle;
      ctx.lineWidth = 1;
      ctx.lineJoin = "miter";
      ctx.beginPath();
      var numPoints = poly.fPoints.length;
      if (numPoints > 0) {
        var point = poly.fPoints[numPoints - 1];
        ctx.moveTo(point.x + xOffset, point.y + yOffset);
        for (var i = 0; i < numPoints; i++) {
          point = poly.fPoints[i];
          ctx.lineTo(point.x + xOffset, point.y + yOffset);
        }
        ctx.closePath();
        ctx.stroke();
        ctx.fill();
      }
      ctx.restore();
    } else if (fillStyle) {
      XML_DrawBoxGroupHighlight(
        xOffset,
        yOffset,
        poly,
        fillStyle,
        altCanvasElement,
        this.GetLayer()
      );
    }
  }

  /**
   * @return {string}
   */
  ObjectName() {
    return "QBoxGroup";
  }

  DoAltRender(altCanvas) {
    var altCanvasType = altCanvas.GetCanvasType();

    switch (altCanvasType) {
      case "SVG":
        this.DoSVGRenderBox(altCanvas);
        break;
      case "XML":
        this.DoXMLRenderBox(altCanvas);
        break;
      default:
        // Should not happen
        break;
    }
  }

  DoSVGRenderBox(altCanvas) {
    var altCanvasElement = altCanvas.GetCanvasElement();
    this.DoRenderSVGBoxGroup(altCanvasElement);
  }

  DoRenderSVGBoxGroup(altCanvasElement) {
    if (!this.fPolyList) {
      return;
    }

    var nPoly = this.fPolyList.length;
    for (var i = 0; i < nPoly; i++) {
      var poly = this.fPolyList[i];
      this.RenderSVGPoly(poly, altCanvasElement);
    }
  }

  RenderSVGPoly(poly, altCanvasElement) {
    var fillStyle = this.fColor;
    var xOffset = this.GetXOffset();
    var yOffset = this.GetYOffset();
    var numPoints = poly.fPoints.length;
    var bounds = [];
    if (numPoints > 0 && fillStyle) {
      for (var i = 0; i < numPoints; i++) {
        var pointX = poly.fPoints[i].x + xOffset;
        var pointY = poly.fPoints[i].y + yOffset;
        bounds.push(pointX);
        bounds.push(pointY);
      }

      var p = SVG_CreateBoxPath(bounds, enumBoxShapes.boxGroup);
      p.setAttribute("fill-opacity", ".5");
      p.setAttribute("fill", fillStyle);
      altCanvasElement.appendChild(p);
    }
  }

  DoXMLRenderBox(altCanvas) {
    var altCanvasElement = altCanvas.GetCanvasElement();

    this.DoRenderBoxGroup(null, true, altCanvasElement);
  }
}
